import { motion } from "framer-motion";
import { ArrowLeft, Phone, ChevronRight, Sparkles, Shield, Fence, Home, Building2, TreePine, Waves, Lock, Wrench } from "lucide-react";
import { Button } from "../components/ui/button";
import { useEffect } from "react";

const LOGO = "/logo-transparent.png";
const QUOTE_URL = "https://tradehq.com.au/albanyfencingcontractors/enquire";
const PHONE_NUMBER = "0438 185 148";

// Services data
const SERVICES = [
  {
    icon: Fence,
    title: "Colorbond Fencing",
    description: "Premium steel fencing in a wide range of modern colours. Durable, low-maintenance, and perfect for residential and commercial properties.",
    features: ["Wide colour range", "Weather resistant", "Low maintenance", "25+ year warranty"]
  },
  {
    icon: Home,
    title: "Aluminium Fencing",
    description: "Sleek, modern aluminium slat fencing and gates. Ideal for contemporary homes and pool areas with a stylish finish.",
    features: ["Modern design", "Rust-proof", "Pool compliant", "Custom colours"]
  },
  {
    icon: TreePine,
    title: "Rural Fencing",
    description: "Strong and reliable fencing solutions for farms and acreages. Post and wire, ringlock, and more.",
    features: ["Stock containment", "Large area coverage", "Durable materials", "Cost effective"]
  },
  {
    icon: Building2,
    title: "Commercial Fencing",
    description: "Heavy-duty security fencing for industrial and commercial properties. Built to withstand the toughest conditions.",
    features: ["High security", "Industrial grade", "Custom heights", "Access control"]
  },
  {
    icon: Waves,
    title: "Pool Fencing",
    description: "Compliant glass and aluminium pool fencing to keep your family safe while maintaining style and visibility.",
    features: ["Safety compliant", "Glass & aluminium", "Self-closing gates", "Child safe"]
  },
  {
    icon: Lock,
    title: "Security Fencing",
    description: "Protect your property with our range of security fencing options including razor wire, chain mesh, and palisade.",
    features: ["Anti-climb design", "CCTV compatible", "24/7 protection", "Custom solutions"]
  },
  {
    icon: Wrench,
    title: "Gates & Automation",
    description: "Custom gates with optional automation for convenient and secure access to your property.",
    features: ["Swing & sliding", "Remote control", "Intercom ready", "Custom designs"]
  },
  {
    icon: Shield,
    title: "Retaining Walls",
    description: "Structural retaining walls to manage slopes and create usable outdoor spaces on your property.",
    features: ["Concrete sleepers", "Limestone blocks", "Drainage solutions", "Engineering certified"]
  },
];

const ServicesPage = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c1829] via-[#162844] to-[#0c1829]">
      {/* Header */}
      <header className="relative z-10 p-4 sm:p-6 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 sm:gap-3">
            <img src={LOGO} alt="Albany Fencing" className="h-10 sm:h-14 w-auto drop-shadow-lg" />
            <div>
              <span className="font-black text-white text-xs sm:text-base tracking-tight block leading-tight">ALBANY FENCING</span>
              <span className="text-cyan-400 text-[10px] sm:text-xs font-bold uppercase tracking-widest">Contractors</span>
            </div>
          </a>
          
          <a href="/">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-4 sm:px-6 py-2 flex items-center gap-2">
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Back to Home</span>
            </Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-cyan-400 text-sm font-bold uppercase tracking-widest">What We Offer</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2 mb-4">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Services</span>
            </h1>
            <p className="text-white/60 text-lg sm:text-xl max-w-2xl mx-auto">
              From residential fencing to commercial security solutions, we deliver quality workmanship across Albany and the Great Southern region.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-cyan-500/30 transition-all duration-300 flex flex-col"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <service.icon className="w-7 h-7 text-cyan-400" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                <p className="text-white/60 text-sm mb-4 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-2 mb-6 flex-grow">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-white/50">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Get Quote CTA */}
                <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="mt-auto">
                  <Button className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white rounded-lg py-3 font-bold text-sm flex items-center justify-center gap-2">
                    Get Quote
                    <ChevronRight size={16} />
                  </Button>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* See Products CTA */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <a href="/products">
              <Button className="bg-transparent hover:bg-white/10 text-white border-2 border-cyan-500/50 hover:border-cyan-400 rounded-full px-10 py-5 font-bold flex items-center justify-center gap-3 mx-auto transition-all duration-300">
                SEE PRODUCTS
                <ChevronRight size={20} />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 relative overflow-hidden">
        {/* Animated fence at bottom */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-around px-8 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="w-2 sm:w-3 rounded-t-sm"
              style={{ background: 'linear-gradient(to top, rgba(16, 185, 129, 0.15), transparent)' }}
              initial={{ height: 0 }}
              whileInView={{ height: `${50 + (i % 4) * 20}px` }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.03 }}
            />
          ))}
        </div>
        <motion.div
          className="absolute left-0 right-0 h-1.5"
          style={{ bottom: '40px', background: 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.12), transparent)' }}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Ready to Get <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Started?</span>
            </h2>
            <p className="text-white/60 mb-8 max-w-lg mx-auto">
              Contact us today for a free, no-obligation quote on any of our fencing services.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}>
                <Button className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white rounded-full px-8 py-5 font-bold flex items-center justify-center gap-3 shadow-lg shadow-emerald-500/25">
                  <Phone size={20} />
                  Call: {PHONE_NUMBER}
                </Button>
              </a>
              <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
                <Button className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-8 py-5 font-bold flex items-center justify-center gap-2">
                  <Sparkles size={18} />
                  Get Free Quote
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Albany Fencing Contractors. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
